/**
 * ScoreComponentsChart — stacked area showing Core / Breadth / Derivatives contribution
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const ScoreComponentsChart = ({ data, indexCode = 'VN30' }) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{t('highVolRegime.noData', 'No data')}</Typography>
      </Box>
    );
  }

  const chartData = data.map(item => {
    const core = Math.min(Math.max(
      (item.atr_ratio >= 1.5 ? 15 : (item.atr_ratio - 1) / 0.5 * 15) +
      (item.wrdd_count_5d * 8) +
      (item.dist_count_20d >= 3 ? 12 : item.dist_count_20d * 4) +
      (item.trend_fragile ? 10 : 0),
    0), 50);
    const breadth = item.breadth?.breadth_weak ? 15 : 0;
    const deriv = item.derivatives?.basis_stress || item.derivatives?.oi_surge ? 10 : 0;
    return {
      date: new Date(item.trade_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      core, breadth, derivatives: deriv, total: item.score || 0,
    };
  });

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', mb: 0.3 }}>{d.date}</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#8b5cf6' }}>{t('highVolRegime.coreSignals', 'Core')}: {d.core.toFixed(1)}</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#06b6d4' }}>{t('highVolRegime.breadthMetrics', 'Breadth')}: {d.breadth.toFixed(1)}</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: '#f59e0b' }}>{t('highVolRegime.derivatives', 'Derivatives')}: {d.derivatives.toFixed(1)}</Typography>
        <Box sx={{ mt: 0.3, pt: 0.3, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700 }}>{t('highVolRegime.charts.total', 'Total')}: {d.total.toFixed(1)}</Typography>
        </Box>
      </Box>
    );
  };

  const LAYERS = [
    { key: 'core', color: '#8b5cf6', label: t('highVolRegime.charts.layerCore', 'Core (ATR, WRDD, Dist)') },
    { key: 'breadth', color: '#06b6d4', label: t('highVolRegime.breadthMetrics', 'Breadth') },
    { key: 'derivatives', color: '#f59e0b', label: t('highVolRegime.charts.layerDerivatives', 'Derivatives (VN30F)') },
  ];

  return (
    <Box sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9' }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
          {indexCode} — {t('highVolRegime.charts.scoreComponents', 'Score Components')}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
          {t('highVolRegime.charts.componentsDesc', 'Breakdown of score contributors')}
        </Typography>
      </Box>

      {/* Chart */}
      <Box sx={{ px: 1, py: 1, flex: 1 }}>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              {LAYERS.map(l => (
                <linearGradient key={l.key} id={`g-${l.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={l.color} stopOpacity={0.7} />
                  <stop offset="95%" stopColor={l.color} stopOpacity={0.15} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
            <YAxis domain={[0, 80]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} width={32} />
            <Tooltip content={<CustomTooltip />} />
            {LAYERS.map(l => (
              <Area key={l.key} type="monotone" dataKey={l.key} stackId="1" stroke={l.color} fill={`url(#g-${l.key})`} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, px: 2, pb: 1.2 }}>
        {LAYERS.map(l => (
          <Box key={l.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ width: 12, height: 3, bgcolor: l.color, borderRadius: 0.5 }} />
            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>{l.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ScoreComponentsChart;
